import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Package, RotateCcw } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

const STATUS_LABELS = {
  placed: { label: "Order placed", color: "bg-neutral-100 text-neutral-700" },
  preparing: { label: "Preparing", color: "bg-amber-100 text-amber-700" },
  ready: { label: "Ready", color: "bg-amber-100 text-amber-700" },
  picked_up: { label: "Picked up", color: "bg-blue-100 text-blue-700" },
  on_the_way: { label: "On the way", color: "bg-blue-100 text-blue-700" },
  delivered: { label: "Delivered", color: "bg-eatup-green/10 text-eatup-green" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { replaceCart } = useCart();

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  // Copy the past order's items into the cart and jump straight to checkout.
  const reorder = (order) => {
    replaceCart(
      {
        restaurant_id: order.restaurant_id,
        name: order.restaurant_name,
        image_url: order.restaurant_image,
        delivery_fee: order.delivery_fee,
        eta_minutes: order.eta_minutes || 25,
      },
      order.items,
    );
    toast.success(`${order.items.length} item${order.items.length !== 1 ? "s" : ""} re-added from ${order.restaurant_name}`);
    navigate("/checkout");
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-32">
      <h1 className="font-display text-4xl font-bold">Your orders</h1>
      <p className="text-neutral-500 mt-1">Every meal you've enjoyed, tracked in one place.</p>

      {loading ? (
        <div className="mt-8 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="mt-16 text-center">
          <div className="mx-auto w-20 h-20 rounded-3xl bg-eatup-green/10 flex items-center justify-center">
            <Package className="w-10 h-10 text-eatup-green" />
          </div>
          <h3 className="mt-6 font-display text-2xl font-bold">No orders yet</h3>
          <p className="text-neutral-500 mt-2">Place your first order and it'll appear here.</p>
          <button onClick={() => navigate("/home")} data-testid="empty-order-cta" className="btn-primary mt-6">Browse restaurants</button>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {orders.map((o) => {
            const s = STATUS_LABELS[o.status] || STATUS_LABELS.placed;
            return (
              <div
                key={o.order_id}
                data-testid={`order-row-${o.order_id}`}
                className="w-full bg-white rounded-2xl card-shadow card-shadow-hover p-4 flex items-center gap-4"
              >
                <button
                  onClick={() => navigate(`/track/${o.order_id}`)}
                  className="flex items-center gap-4 flex-1 min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-eatup-green rounded-xl"
                  aria-label={`Track order ${o.order_id}`}
                >
                  <img src={o.restaurant_image} alt={o.restaurant_name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold truncate">{o.restaurant_name}</div>
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1 truncate">
                      {o.items.length} items · ${o.total.toFixed(2)} · #{o.order_id.slice(-6).toUpperCase()}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                </button>
                <button
                  onClick={() => reorder(o)}
                  data-testid={`reorder-btn-${o.order_id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white text-xs font-black uppercase tracking-widest px-4 py-2.5 shadow-sm transition active:scale-[0.98]"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reorder
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
